//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useRef, useLayoutEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import IconButton from '@material-ui/core/IconButton';
import ShareIcon from '@material-ui/icons/Share';
// import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SimpleBar from 'simplebar-react';
import { Link /*DirectLink, Element, Events, animateScroll as scroll, scrollSpy, scroller*/ } from 'react-scroll';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from '@material-ui/lab';
import InfoIcon from '@material-ui/icons/Info';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideocamIcon from '@material-ui/icons/Videocam';
import LinkIcon from '@material-ui/icons/Link';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _throttle from 'lodash/throttle';
import _some from 'lodash/some';
import _forEach from 'lodash/forEach';
import _isNil from 'lodash/isNil';
import _noop from 'lodash/noop';
import _mapKeys from 'lodash/mapKeys';

import { differenceInDays } from 'date-fns';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { unsubscribeRealtimeUpdates, logEvent, logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import {
    DASHBOARD_MY_PROFILE,
    PROFILE_TYPE_PUBLIC,
    PROFILE_TYPE_DRAFT,
    PROFILE_ABOUT_ME,
    PROFILE_RESUME,
    PROFILE_VIDEO_OVERVIEW,
    PROFILE_SOCIAL_MEDIA,
    SCROLL_TO_ELEMENT_ABOUT_ME,
    SCROLL_TO_ELEMENT_RESUME,
    SCROLL_TO_ELEMENT_VIDEO_OVERVIEW,
    SCROLL_TO_ELEMENT_SOCIAL_MEDIA_PORTFOLIO,
    UPLOADED_VIDEO_STATUS_PROCESSING,
    UPLOADED_VIDEO_STATUS_FAILED,
    PAGE_TITLE_JS_MY_PROFILE,
    ANALYTICS_EVENT_TALENT_PROFILE_PREVIEW,
    ANALYTICS_EVENT_TALENT_PROFILE_PUBLISHED,
    CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
} from '../common/constants';
import { getFormattedDate } from '../common/dateHelper';
import { useElementDimensions } from '../common/scrollHelper';
import { getUserDateFormat } from '../common/dateHelper';
import { loadProfileByUserId, updateMyProfileReminderDts } from '../common/services/dashboardServices';
// import { getTalentPageUrlPathById } from '../common/linkHelper';
import { changeProfileDraft } from '../common/services/myProfileServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveProfileType, resetVideoOverviewStatus } from '../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
import AfitdScrollToElement from '../common/elemental/AfitdScrollToElement';
import ProfileDialog from '../empDashboardMyJobs/subComponents/ProfileDialog';
import AfitdButtonGroup from '../common/elemental/AfitdButtonGroup';
import AfitdButton from '../common/elemental/AfitdButton';
import AfitdConfirmDialog from '../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../common/elemental/AfitdMessageDialog';
import AfitdTour from '../common/elemental/AfitdTour';
import AfitdTourStepContent from '../common/elemental/AfitdTourStepContent';
import AfitdTourStepEndContent from '../common/elemental/AfitdTourStepEndContent';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import AboutMe from './subComponents/AboutMe';
import Resume from './subComponents/Resume';
import VideoOverview from './subComponents/VideoOverview';
import SocialMediaPortfolio from './subComponents/SocialMediaPortfolio';
import ShareButtonBar from './subComponents/ShareButtonBar';
import ShareButtonMenu from './subComponents/ShareButtonMenu';
import Footer from '../dashboard/subComponents/Footer';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            height: '100%',
            background: '#FAFAFC',
        },
        header: {
            height: '128px',
            [theme.breakpoints.down('md')]: { height: '100px' },
            [theme.breakpoints.down('sm')]: { height: '128px' },
        },
        content: {
            flex: 1,
            overflowY: 'hidden',
            marginTop: 0,
            marginBottom: 0,
            borderRadius: 10,
            backgroundColor: '#fff',
        },
        profileLeft: {
            textAlign: 'left',
            padding: '40px 0 40px 50px',
            [theme.breakpoints.down('lg')]: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '15px 40px 0px 40px' },
            [theme.breakpoints.down('sm')]: { padding: '10px 15px 0px 15px' },
        },
        profileRight: {
            overflowY: 'auto',
            height: '100%',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },

        // root: {
        //     height: '100%',
        //     display: 'flex',
        //     flexDirection: 'column',
        //     background: '#FAFAFC',
        // },
        // header: {
        //     width: '100%',
        //     minHeight: '128px',
        //     marginRight: '64px',
        //     display: 'flex',
        //     flexDirection: 'row',
        // },
        headerLeft: {
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
        },
        headerRight: {
            display: 'flex',
            flexGrow: 4,
            justifyContent: 'flex-end',
        },
        headerTitle: {
            marginTop: '38px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            [theme.breakpoints.down('md')]: { marginTop: '10px' },
        },
        headerToday: {
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        headerButtons: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        todayIcon: {
            fontSize: '16px',
            color: 'rgba(17, 30, 20, 0.4)',
        },
        todayContainer: {
            marginTop: '1px',
            marginLeft: '9px',
            fontSize: '13px',
            color: 'rgba(17, 30, 20, 0.6)',
        },
        title: {
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: '26px',
            lineHeight: '30px',
            color: '#111E14',
            [theme.breakpoints.down('sm')]: { fontSize: 24 },
        },
        shareContainer: {
            marginTop: '4px',
            marginLeft: '24px',
            display: 'flex',
        },
        shareIcon: {
            color: 'rgba(17, 30, 20, 0.4)',
            padding: '0 0 0 0',
        },
        shareIconClickable: {
            '&:hover': {
                cursor: 'pointer',
                color: '#111E14',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
        },
        shareText: {
            marginLeft: '20px',
            marginRight: '10px',
        },
        shareTextClickable: {
            '&:hover': {
                cursor: 'pointer',
            },
        },
        slideContainer: {
            overflow: 'hidden',
        },

        // body: {
        //     height: '100%',
        //     background: '#ffffff',
        //     borderRadius: '10px',
        //     marginRight: '64px',
        //     padding: '0px',
        //     display: 'flex',
        // },
        detailContainer: {
            padding: '40px',
            [theme.breakpoints.down('lg')]: { paddingTop: '30px', paddingBottom: 95 },
            [theme.breakpoints.down('sm')]: { padding: '15px', paddingBottom: 70 },
        },
        publicDraftButtonGroup: {
            '& :not(:last-child)': {
                borderRight: 'none',
            },
        },
        publicDraftButtons: {
            background: '#1D8A8B',
            color: '#FFF',
            // textTransform: 'none',
            '&:hover': {
                backgroundColor: '#E0E1E5',
                color: '#000',
            },
        },
        publicDraftButtonOff: {
            background: 'rgba(0, 0, 0, 0.03)',
            color: '#111E14',
        },
        viewProfileButtonContainer: {
            marginLeft: '20px',
        },
        viewProfileButton: {
            textTransform: 'none',
        },
        // profileLeft: {
        //     maxWidth: '195px',
        //     textAlign: 'left',
        //     width: '25%',
        //     padding: '40px 0 40px 40px',
        // },
        // profileRight: {
        //     textAlign: 'left',
        //     width: '75%',
        //     height: '100%',
        // },
        helpOutlineIcon: {
            fontSize: '18px',
            backgroundColor: '#F0F2F6',
            padding: '8px',
            borderRadius: '50%',
            marginRight: '20px',
            marginTop: '2px',
        },
        profileLink: {
            fontSize: '16px',
            lineHeight: '18px',
            // color: '#D5D7DD',
            color: 'rgba(17, 30, 20, 0.4)',
            fontWeight: '500',
            letterSpacing: '0px',
            padding: '10px 0 9px 0',
            '&:hover': {
                cursor: 'pointer',
                color: '#111E14',
            },
            [theme.breakpoints.down('md')]: { fontSize: '14pt' },
        },
        profileLinkSelected: {
            fontSize: '16px',
            lineHeight: '18px',
            color: '#111E14',
            fontWeight: '600',
            letterSpacing: '0px',
            padding: '10px 0 9px 0',
            '&:hover': {
                cursor: 'pointer',
                color: '#111E14',
            },
            [theme.breakpoints.down('md')]: { fontSize: '14pt' },
        },
        profileLeftDivider: {
            padding: '16px 40px 0 40px',
            [theme.breakpoints.down('sm')]: { padding: '0px', paddingBottom: 2 },
        },
        editSpeedDial: {
            position: 'absolute',
            [theme.breakpoints.up('md')]: { bottom: theme.spacing(4), right: theme.spacing(10) },
            [theme.breakpoints.down('md')]: { bottom: theme.spacing(8), right: theme.spacing(10) },
            [theme.breakpoints.down('sm')]: { bottom: theme.spacing(10), right: theme.spacing(2) },
            [theme.breakpoints.down('xs')]: { bottom: theme.spacing(10), right: theme.spacing(2) },
        },
        editSpeedDialAction: {
            color: '#fff',
            backgroundColor: '#000',
        },
        actionFab: { width: 56, height: 56 },
        editSpeedDialTooltipLabel: {
            color: '#fff',
            backgroundColor: '#000',
            fontSize: 14,
            whiteSpace: 'nowrap',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        classes: {
            publicDraftButtons: publicDraftButtonsCls,
            publicDraftButtonOff: publicDraftButtonOffCls,
            profileLink: profileLinkCls,
            profileLinkSelected: profileLinkSelectedCls,
            shareIcon: shareIconCls,
            shareIconClickable: shareIconClickableCls,
            shareText: shareTextCls,
            shareTextClickable: shareTextClickableCls,
        },
        // props,
        userDateFormat,
        currentUser,
        profileType,
        // setProfileType,
        sectionInView,
        profileData,
        scrollableNodeRef,
        handleClickProfileTypeButton,
        confirmDialogProfileType,
        // setConfirmDialogProfileType,
        handleConfirmDialogCancelButton,
        handleConfirmDialogOkButton,
        handleClickViewProfileButton,
        profileDialogOpen,
        setProfileDialogOpen,
        shareButtonsShowing,
        setShareButtonsShowing,
        // smMatches,
        // userId,
        openMinOneResumeRequiredDialog,
        setOpenMinOneResumeRequiredDialog,
        uploadedVideoStatus,
        uploadedVideoStats,
        openBadContentVideoDialog,
        // setOpenBadContentVideoDialog,
        badVideoDialogMessage,
        isVideoProcessing,
        isVideoFailedTest,
        handleCloseBadContentDialog,
        walkThroughTourSteps,
        makeProfilePublicTourSteps,
        shareProfileTourSteps,
        walkThroughTourOpen,
        makeProfilePublicTourOpen,
        shareProfileTourOpen,
        handleCloseTour,
        editAboutMeDialogOpen,
        setEditAboutMeDialogOpen,
        editResumeDialogOpen,
        setEditResumeDialogOpen,
        editVideoOverviewDialogOpen,
        setEditVideoOverviewDialogOpen,
        editPortfolioDialogOpen,
        setEditPortfolioDialogOpen,
        confirmEditProfileDialogOpen,
        setConfirmEditProfileDialogOpen,
        handleConfirmEditProfile,
    } = params;

    // const { } = props;

    const isPublic = profileType === PROFILE_TYPE_PUBLIC;
    const isDraft = profileType === PROFILE_TYPE_DRAFT;

    const tourOpen = walkThroughTourOpen || makeProfilePublicTourOpen || shareProfileTourOpen;
    const tourSteps = walkThroughTourOpen ? walkThroughTourSteps : makeProfilePublicTourOpen ? makeProfilePublicTourSteps : shareProfileTourOpen ? shareProfileTourSteps : [];

    const defns = {
        root: { container: true, direction: 'column' },
        content: { container: true, item: true, spacing: 0 },
        header: { item: true, container: true },
        profileLeft: { item: true, xl: 3, lg: 12, md: 12, sm: 12, xs: 12 },
        profileRight: { item: true, xl: 9, lg: 12, md: 12, sm: 12, xs: 12 },
        profileLeftDivider: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },

        shareIcon: {
            className: cx(shareIconCls, { [shareIconClickableCls]: isPublic }),
            onClick: isPublic ? setShareButtonsShowing.bind(null, !shareButtonsShowing) : _noop,
        },
        shareText: {
            variant: 'body2',
            value: 'Share',
            className: cx(shareTextCls, { [shareTextClickableCls]: isPublic }),
            onClick: isPublic ? setShareButtonsShowing.bind(null, !shareButtonsShowing) : _noop,
        },
        shareButtonBar: {
            onClose: setShareButtonsShowing.bind(null, false),
        },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },
        publicDraftButtonGroup: { disableElevation: true, disabled: isVideoProcessing || isVideoFailedTest },
        publicButton: {
            className: cx({ [publicDraftButtonOffCls]: isDraft || !isPublic, [publicDraftButtonsCls]: isPublic }),
            onClick: isPublic ? _noop : handleClickProfileTypeButton.bind(null, PROFILE_TYPE_PUBLIC, profileData), //setProfileType.bind(null, PROFILE_TYPE_PUBLIC),
        },
        draftButton: {
            className: cx({ [publicDraftButtonOffCls]: isPublic || !isDraft, [publicDraftButtonsCls]: isDraft }),
            onClick: isDraft ? _noop : handleClickProfileTypeButton.bind(null, PROFILE_TYPE_DRAFT, profileData), //setProfileType.bind(null, PROFILE_TYPE_DRAFT),
        },
        viewProfileButtonGroup: { disableElevation: true },
        viewProfileButton: {
            color: 'primary',
            onClick: handleClickViewProfileButton,
            // onClick: !smMatches ? undefined : handleClickViewProfileButton,
            // href: !smMatches ? getTalentPageUrlPathById(userId) : undefined,
            // target: !smMatches ? '_blank' : undefined,
        },
        profileDialog: {
            open: profileDialogOpen,
            onClose: () => setProfileDialogOpen(false),
            profileData,
        },

        aboutMeLink: {
            to: SCROLL_TO_ELEMENT_ABOUT_ME,
            smooth: true,
            container: scrollableNodeRef.current,
            duration: 500,
        },
        resumeLink: {
            to: SCROLL_TO_ELEMENT_RESUME,
            smooth: true,
            container: scrollableNodeRef.current,
            duration: 500,
        },
        videoLink: {
            to: SCROLL_TO_ELEMENT_VIDEO_OVERVIEW,
            smooth: true,
            container: scrollableNodeRef.current,
            duration: 500,
        },
        socialLink: {
            to: SCROLL_TO_ELEMENT_SOCIAL_MEDIA_PORTFOLIO,
            smooth: true,
            container: scrollableNodeRef.current,
            duration: 500,
        },
        aboutMeLinkContainer: {
            className: sectionInView === SCROLL_TO_ELEMENT_ABOUT_ME ? profileLinkSelectedCls : profileLinkCls,
        },
        resumeLinkContainer: {
            className: sectionInView === SCROLL_TO_ELEMENT_RESUME ? profileLinkSelectedCls : profileLinkCls,
        },
        videoLinkContainer: {
            className: sectionInView === SCROLL_TO_ELEMENT_VIDEO_OVERVIEW ? profileLinkSelectedCls : profileLinkCls,
        },
        socialLinkContainer: {
            className: sectionInView === SCROLL_TO_ELEMENT_SOCIAL_MEDIA_PORTFOLIO ? profileLinkSelectedCls : profileLinkCls,
        },
        aboutMe: {
            data: getMyProfileSectionData(currentUser, ['firstName', 'middleName', 'lastName', 'country', 'state', 'city'], profileData, [
                'gender',
                'race',
                'employmentType',
                'isExactRate',
                'rateMin',
                'rateMax',
                'currency',
                'currencySymbol',
                'payFrequency',
                'occupation',
                'skillset',
                'education',
                'otherEducation',
                'aboutMe',
                'yearsOfExperience',
                'willingRelocate',
                'isPublic',
            ]),
            editDialogOpen: editAboutMeDialogOpen,
            setEditDialogOpen: setEditAboutMeDialogOpen,
        },
        resume: {
            data: {
                resumeList: _get(profileData, 'resumeList'),
                isPublic: _get(profileData, 'isPublic'),
            },
            editDialogOpen: editResumeDialogOpen,
            setEditDialogOpen: setEditResumeDialogOpen,
        },
        videoOverview: {
            data: {
                isPublic: _get(profileData, 'isPublic'),
                uploadedVideoStatus,
                uploadedVideoStats,
                videoOverviewDuration: _get(profileData, 'videoOverviewDuration'),
                videoOverviewUrl: _get(profileData, 'videoOverviewUrl'),
                videoOverviewName: _get(profileData, 'videoOverviewName'),
                videoOverviewKey: _get(profileData, 'videoOverviewKey'),
                videoOverviewSize: _get(profileData, 'videoOverviewSize'),
                isVideoProcessing,
                isVideoFailedTest,
                badContentDetected: badVideoDialogMessage.join(','),
            },
            editDialogOpen: editVideoOverviewDialogOpen,
            setEditDialogOpen: setEditVideoOverviewDialogOpen,
        },
        socialMediaPortfolio: {
            data: {
                isPublic: _get(profileData, 'isPublic'),
                facebookUrl: _get(profileData, 'facebookUrl'),
                linkedInUrl: _get(profileData, 'linkedInUrl'),
                youTubeUrl: _get(profileData, 'youTubeUrl'),
                instagramUrl: _get(profileData, 'instagramUrl'),
                twitterUrl: _get(profileData, 'twitterUrl'),
                websiteUrl: _get(profileData, 'websiteUrl'),
                tikTokUrl: _get(profileData, 'tikTokUrl'),
                gitHubUrl: _get(profileData, 'gitHubUrl'),
            },
            editDialogOpen: editPortfolioDialogOpen,
            setEditDialogOpen: setEditPortfolioDialogOpen,
        },
        confirmDialog: {
            open: !_isNil(confirmDialogProfileType),
            title: getConfirmDialogTitle(confirmDialogProfileType === PROFILE_TYPE_PUBLIC),
            message: getConfirmDialogMessage(confirmDialogProfileType === PROFILE_TYPE_PUBLIC),
        },
        confirmDialogCancelButton: {
            onClick: handleConfirmDialogCancelButton,
        },
        confirmDialogOkButton: {
            onClick: handleConfirmDialogOkButton,
        },
        // minOneResumeRequiredDialog: {
        //     maxWidth: 'sm',
        //     title: 'Make Profile Public?',
        //     onClose: () => setOpenMinOneResumeRequiredDialog(false),
        //     yesButtonLabel: 'OK',
        //     noButtonLabel: 'Cancel',
        //     open: openMinOneResumeRequiredDialog,
        //     message: `To make your profile public you must upload at least one resume to your profile`,
        // },
        minOneResumeRequiredDialog: {
            title: 'Incomplete Profile Information',
            message: `To help find the right jobs for you, before making your profile public, you must enter all the required fields in About Me and upload at least one resume to your profile.`,
            open: openMinOneResumeRequiredDialog,
            onClose: () => setOpenMinOneResumeRequiredDialog(false),
        },
        badContentDialog: {
            title: 'Inappropriate Content is not allowed!',
            onClose: handleCloseBadContentDialog,
            yesButtonLabel: 'OK',
            open: openBadContentVideoDialog,
            children: (
                <AfitdTypography>
                    We detected <b>{badVideoDialogMessage.join(',')}</b> in your video which does not meet our community guidelines.
                    <br /> Inappropriate videos cannot be saved to this platform.
                </AfitdTypography>
            ),
        },
        walkThroughTour: {
            steps: tourSteps,
            open: tourOpen,
            isHidden: !tourOpen,
            onClose: handleCloseTour,
        },
        confirmEditProfileDialog: {
            maxWidth: 'sm',
            title: 'Edit Your Profile?',
            onClose: () => setConfirmEditProfileDialogOpen(false),
            onConfirm: handleConfirmEditProfile,
            open: confirmEditProfileDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getConfirmDialogTitle = isPublic => {
    return isPublic ? 'Make Your Profile Public?' : 'Change Your Profile to Draft?';
};

const getConfirmDialogMessage = isPublic => {
    return isPublic
        ? 'Your profile will be live and available to be reviewed and searched by employers.'
        : 'Your profile will no longer be available to be reviewed and searched by employers.';
};

const getMyProfileSectionData = (currentUser, userFieldNames, profileData, fieldNames = []) => {
    let data = {};
    _forEach(userFieldNames, fieldName => {
        _set(data, fieldName, _get(currentUser, fieldName));
    });
    _forEach(fieldNames, fieldName => {
        _set(data, fieldName, _get(profileData, fieldName));
    });
    return data;
};

const handleScrollDetailPane = (scrollableNodeDimensions, sections = [], setSectionInView) => {
    let elementInViewIdx = 0;
    let elementInView = SCROLL_TO_ELEMENT_ABOUT_ME;

    // dimensions of the scrollable container
    const { height = 0, top = 0 } = scrollableNodeDimensions;

    // set a line for us to determine whether a section is "in view"
    const inViewTop = height / 2 + top;

    // scan from bottom up, see if a section is in view (stop at the first one found)
    _some(sections, (section, idx) => {
        const { node, name } = section;
        if (node) {
            // console.log(`>>>>>>>>>>> inViewTop: ${inViewTop} / ${name}`, node.getBoundingClientRect());
            const sectionDimensions = node.getBoundingClientRect();
            const { top = 0 } = sectionDimensions;
            if (top < inViewTop) {
                // console.log(`_________________________________ ${idx} ${name} in view!`);
                elementInView = name;
                elementInViewIdx = idx;
                return true;
            }
            return false;
        }
    });

    // if not the first section, check if the title of section above is still in view.  If so, that section is in view!
    if (elementInViewIdx < sections.length - 1) {
        const prevSection = sections[elementInViewIdx + 1];
        const { node: prevSectionNode, name: prevSectionNodeName } = prevSection;
        const { top: prevSectionTop = 0 } = prevSectionNode.getBoundingClientRect();
        // console.log(`_________________________________ prevSectionTop: ${prevSectionTop} / top: ${top} / elementInViewIdx: ${elementInViewIdx} / `, sections);
        if (prevSectionTop >= top) {
            elementInView = prevSectionNodeName;
        }
    }

    setSectionInView(elementInView);
};

const getHandleScrollDetailPane = _throttle(handleScrollDetailPane, 500, { leading: true });

const getTourStepMakeProfilePublicReminder = handleCloseTour => {
    return {
        selector: '[data-tour="js-my-profile-public-button-container"]',
        content: (
            <AfitdTourStepContent
                title="Don't forget to make your profile 'Public'"
                message="Changing your status to 'Public' will make your profile available to be reviewed and searched by employers."
                gotItOnClick={handleCloseTour}
            />
        ),
    };
};

const getTourStepViewProfile = handleCloseTour => {
    return {
        selector: '[data-tour="js-view-profile-button-container"]',
        content: (
            <AfitdTourStepContent
                title="Preview what employers will see"
                message="Before making your profile 'Public', click this button to see what your profile will look like."
                gotItOnClick={handleCloseTour}
            />
        ),
    };
};

const getTourStepShareProfile = handleCloseTour => {
    return {
        selector: '[data-tour="js-share-button-container"]',
        content: (
            <AfitdTourStepContent
                title="Sharing is caring"
                message="When your profile is 'Public' you can amplify your profile awareness via your social media channels."
                gotItOnClick={handleCloseTour}
            />
        ),
    };
};

const getTourStepWalkThroughEnd = handleCloseTour => {
    return {
        // selector: '[data-tour="walk-through-end"]',
        content: (
            <AfitdTourStepEndContent
                title="Walk-through completed!"
                message="All the best in your job search and we are here to help. Reach out to us via the 'Contact Us' page if you have any issues with our software."
                doneOnClick={handleCloseTour}
            />
        ),
    };
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MyProfile = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const userId = _get(appContext, 'userId');
    const currentUser = _get(appContext, 'currentUser');
    const userDateFormat = getUserDateFormat(appContext);

    const context = useContext(LayoutContext);
    const setSnackbarMessage = _get(context, 'setSnackbarMessage');
    const setSnackbarErrorMessage = _get(context, 'setSnackbarErrorMessage');
    const dashboardData = _get(context, 'dashboardData');
    const { setMainBackdropOpen } = context;

    const scrollableNodeRef = useRef();
    const aboutMeScrollToElementRef = useRef();
    const resumeScrollToElementRef = useRef();
    const videoOverviewScrollToElementRef = useRef();
    const socialMediaScrollToElementRef = useRef();
    const scrollableNodeDimensions = useElementDimensions(_get(scrollableNodeRef, 'current'));

    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [profileType, setProfileType] = useState();
    const [profileData, setProfileData] = useState();
    const [sectionInView, setSectionInView] = useState(SCROLL_TO_ELEMENT_ABOUT_ME);
    const [confirmDialogProfileType, setConfirmDialogProfileType] = useState(null);
    const [shareButtonsShowing, setShareButtonsShowing] = useState(false);
    const [profileDialogOpen, setProfileDialogOpen] = useState(false);
    const [openMinOneResumeRequiredDialog, setOpenMinOneResumeRequiredDialog] = useState(false);
    const [openBadContentVideoDialog, setOpenBadContentVideoDialog] = useState(false);
    const [badVideoDialogMessage, setBadVideoDialogMessage] = useState([]);
    const [walkThroughTourOpen, setWalkThroughTourOpen] = useState(false);
    const [makeProfilePublicTourOpen, setMakeProfilePublicTourOpen] = useState(false);
    const [shareProfileTourOpen, setShareProfileTourOpen] = useState(false);
    const [editSpeedDialOpen, setEditSpeedDialOpen] = useState();
    const [editAboutMeDialogOpen, setEditAboutMeDialogOpen] = useState();
    const [editResumeDialogOpen, setEditResumeDialogOpen] = useState();
    const [editVideoOverviewDialogOpen, setEditVideoOverviewDialogOpen] = useState();
    const [editPortfolioDialogOpen, setEditPortfolioDialogOpen] = useState();
    const [confirmEditProfileDialogOpen, setConfirmEditProfileDialogOpen] = useState(false);

    // share button menu state and handler
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleOpenMenu = event => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isProfilePublic = _get(profileData, 'isPublic');
    const uploadedVideoStatus = _get(profileData, 'uploadedVideoStatus');
    const uploadedVideoStats = _get(profileData, 'uploadedVideoStats');
    const isBadContent = Boolean(uploadedVideoStatus === UPLOADED_VIDEO_STATUS_FAILED);
    const isVideoProcessing = Boolean(UPLOADED_VIDEO_STATUS_PROCESSING === _get(profileData, 'uploadedVideoStatus'));
    const isVideoFailedTest = Boolean(UPLOADED_VIDEO_STATUS_FAILED === _get(profileData, 'uploadedVideoStatus'));

    const showMakeProfilePublicTourStepDt = _get(dashboardData, 'showMakeProfilePublicTourStepDt');
    const showShareProfileTourStepDt = _get(dashboardData, 'showShareProfileTourStepDt');
    const hasActiveResume = _some(_get(profileData, 'resumeList'), resumeItem => resumeItem.isActive);

    // never seen make-public reminder tour step and profile is Draft
    const isShowWalkThrough = profileData && !profileData.isPublic && _isNil(showMakeProfilePublicTourStepDt);

    // never seen share tour step
    const isShowFirstTimeShareTourStep = !isShowWalkThrough && profileData && _isNil(showShareProfileTourStepDt);

    // returning visitor with an active resume has not seen the reminder message for more than a week and profile is Draft
    const isShowMakePublicReminderAgain =
        !isShowWalkThrough && profileData && !profileData.isPublic && differenceInDays(new Date(), showMakeProfilePublicTourStepDt) > 7 && hasActiveResume;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_JS_MY_PROFILE);
    }, []);

    useEffect(() => {
        let isUpdateTimestamp = false;

        if (isShowWalkThrough || isShowFirstTimeShareTourStep || isShowMakePublicReminderAgain) {
            isShowWalkThrough && setWalkThroughTourOpen(true);
            isShowFirstTimeShareTourStep && setShareProfileTourOpen(true);
            isShowMakePublicReminderAgain && setMakeProfilePublicTourOpen(true);
            isUpdateTimestamp = true;
        }

        if (isUpdateTimestamp) {
            updateMyProfileReminderDts({ userId, isShowWalkThrough, isShowFirstTimeShareTourStep, isShowMakePublicReminderAgain });
        }
        // console.log('Temporary avoid linter warnings', setProfileMakePublicReminderDialogOpen, updateMyProfileReminderDts);
    }, [userId, isShowWalkThrough, isShowFirstTimeShareTourStep, isShowMakePublicReminderAgain]);

    useEffect(() => {
        setOpenBadContentVideoDialog(isBadContent);
        let arr = [];
        _mapKeys(uploadedVideoStats, (value, key) => {
            // console.log({ value, key });
            if (value >= 0.05) arr.push(key);
        });
        // console.log({ arr });
        setBadVideoDialogMessage(arr);
    }, [isBadContent, uploadedVideoStats]);

    useEffect(() => {
        let unsubscribeOnSnapshot = null;
        if (userId) {
            // Load profile data
            unsubscribeOnSnapshot = loadProfileByUserId(userId, setProfileData);
        }
        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeOnSnapshot]);
        };
    }, [userId]);

    useEffect(() => {
        if (profileData) {
            const isPublic = _get(profileData, 'isPublic');
            setProfileType(isPublic ? PROFILE_TYPE_PUBLIC : PROFILE_TYPE_DRAFT);
        }
    }, [profileData]);

    useLayoutEffect(() => {
        // once scrollableNode is available, set up the scroll event listener
        const scrollableNode = scrollableNodeRef.current;
        const sections = [
            { node: socialMediaScrollToElementRef.current, name: SCROLL_TO_ELEMENT_SOCIAL_MEDIA_PORTFOLIO },
            { node: videoOverviewScrollToElementRef.current, name: SCROLL_TO_ELEMENT_VIDEO_OVERVIEW },
            { node: resumeScrollToElementRef.current, name: SCROLL_TO_ELEMENT_RESUME },
            { node: aboutMeScrollToElementRef.current, name: SCROLL_TO_ELEMENT_ABOUT_ME },
        ];
        const handleScrollDetailPane = getHandleScrollDetailPane.bind(null, scrollableNodeDimensions, sections, setSectionInView);

        if (scrollableNode) {
            scrollableNode.addEventListener('scroll', handleScrollDetailPane);
        }

        return () => {
            if (scrollableNode) {
                scrollableNode.removeEventListener('scroll', handleScrollDetailPane);
            }
        };
    }, [scrollableNodeRef, scrollableNodeDimensions]);

    // if profile is set to DRAFT while the share menu is open, close the share menu.
    useEffect(() => {
        if (profileType === PROFILE_TYPE_DRAFT && shareButtonsShowing) {
            setShareButtonsShowing(false);
        }
    }, [profileType, shareButtonsShowing]);

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const setAboutMeScrollToElementRef = useCallback(ref => {
        aboutMeScrollToElementRef.current = ref;
    }, []);
    const setResumeScrollToElementRef = useCallback(ref => {
        resumeScrollToElementRef.current = ref;
    }, []);
    const setVideoOverviewScrollToElementRef = useCallback(ref => {
        videoOverviewScrollToElementRef.current = ref;
    }, []);
    const setSocialMediaScrollToElementRef = useCallback(ref => {
        socialMediaScrollToElementRef.current = ref;
    }, []);

    const isMissingRequiredFields = profileData => {
        const fieldNameList = ['firstName', 'lastName', 'gender', 'race', 'country', 'state', 'city', 'yearsOfExperience', 'occupation'];
        return _some(fieldNameList, fieldName => _isNil(profileData?.[fieldName]));
    };

    const handleClickProfileTypeButton = useCallback((profileType, profileData) => {
        if (profileData) {
            let haveActiveResume = false;
            const resumeList = _get(profileData, 'resumeList');
            resumeList.forEach(resume => {
                if (resume.isActive) haveActiveResume = true;
            });
            if (isMissingRequiredFields(profileData) || (profileType === PROFILE_TYPE_PUBLIC && !haveActiveResume)) setOpenMinOneResumeRequiredDialog(true);
            else setConfirmDialogProfileType(profileType);
        }
    }, []);

    const handleConfirmDialogCancelButton = useCallback(() => {
        setConfirmDialogProfileType(null);
    }, []);

    const handleConfirmDialogOkButton = useCallback(async () => {
        const isPublic = confirmDialogProfileType === PROFILE_TYPE_PUBLIC;

        // update profile type before save otherwise it will re-render to previous value before db value is updated
        setProfileType(confirmDialogProfileType);

        // call service
        const success = await saveProfileType({
            userId,
            isPublic,
            dataDidSave: () => {
                // setProfileType(confirmDialogProfileType);
                setConfirmDialogProfileType(null);
                isPublic && logEvent(ANALYTICS_EVENT_TALENT_PROFILE_PUBLISHED);
            },
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });

        // if save failed, revert the profile type
        if (!success) setProfileType(isPublic ? PROFILE_TYPE_DRAFT : PROFILE_TYPE_PUBLIC);
        ////
    }, [userId, setSnackbarMessage, setSnackbarErrorMessage, confirmDialogProfileType]);

    const handleClickViewProfileButton = useCallback(() => {
        setProfileDialogOpen(true);
        logEvent(ANALYTICS_EVENT_TALENT_PROFILE_PREVIEW);
    }, []);

    const handleCloseBadContentDialog = async () => {
        await resetVideoOverviewStatus({ userId, setSnackbarMessage, setSnackbarErrorMessage });
        setOpenBadContentVideoDialog(false);
    };

    const handleCloseTour = useCallback(() => {
        setWalkThroughTourOpen(false);
        setMakeProfilePublicTourOpen(false);
        setShareProfileTourOpen(false);
    }, []);

    // }, [userId, isShowWalkThrough, isShowFirstTimeShareTourStep, isShowMakePublicReminderAgain]);
    const walkThroughTourSteps = [getTourStepMakeProfilePublicReminder(), getTourStepViewProfile(), getTourStepShareProfile(), getTourStepWalkThroughEnd(handleCloseTour)];
    const makeProfilePublicTourSteps = [getTourStepMakeProfilePublicReminder(handleCloseTour)];
    const shareProfileTourSteps = [getTourStepShareProfile(handleCloseTour)];

    const handleConfirmEditProfile = async () => {
        const success = await changeProfileDraft({ userId, setSnackbarMessage, setSnackbarErrorMessage });
        if (success) {
            setEditSpeedDialOpen(true);
            setMainBackdropOpen(true);
        }
    };

    const handleCloseEditSpeedDial = (evt, reason) => {
        if (reason === 'toggle' || reason === 'blur') {
            setEditSpeedDialOpen(false);
            setMainBackdropOpen(false);
        }
    };

    const handleOpenEditSpeedDial = (evt, reason) => {
        if (reason === 'toggle') {
            if (isProfilePublic) {
                setConfirmEditProfileDialogOpen(true);
            } else {
                setEditSpeedDialOpen(true);
                setMainBackdropOpen(true);
            }
        }
    };

    const handleClickEditAboutMeSpeedDial = () => {
        setEditAboutMeDialogOpen(true);
    };

    const handleClickEditResumeSpeedDial = () => {
        setEditResumeDialogOpen(true);
    };

    const handleClickEditVideoOverviewSpeedDial = () => {
        setEditVideoOverviewDialogOpen(true);
    };

    const handleClickEditPortfolioSpeedDial = () => {
        setEditPortfolioDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const editSpeedDialActions = [
        { icon: <LinkIcon />, name: 'Social Media & Portfolio', onClick: handleClickEditPortfolioSpeedDial },
        { icon: <VideocamIcon />, name: 'Video Overview', onClick: handleClickEditVideoOverviewSpeedDial },
        { icon: <InsertDriveFileIcon />, name: 'Resume', onClick: handleClickEditResumeSpeedDial },
        { icon: <InfoIcon />, name: 'About Me', onClick: handleClickEditAboutMeSpeedDial },
    ];

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const defns = getDefns({
        classes,
        props,
        userDateFormat,
        currentUser,
        profileType,
        setProfileType,
        sectionInView,
        profileData,
        scrollableNodeRef,
        handleClickProfileTypeButton,
        confirmDialogProfileType,
        setConfirmDialogProfileType,
        handleConfirmDialogCancelButton,
        handleConfirmDialogOkButton,
        handleClickViewProfileButton,
        profileDialogOpen,
        setProfileDialogOpen,
        shareButtonsShowing,
        setShareButtonsShowing,
        smMatches,
        userId,
        openMinOneResumeRequiredDialog,
        setOpenMinOneResumeRequiredDialog,
        uploadedVideoStatus,
        uploadedVideoStats,
        openBadContentVideoDialog,
        setOpenBadContentVideoDialog,
        badVideoDialogMessage,
        isVideoProcessing,
        isVideoFailedTest,
        handleCloseBadContentDialog,
        walkThroughTourSteps,
        makeProfilePublicTourSteps,
        shareProfileTourSteps,
        walkThroughTourOpen,
        makeProfilePublicTourOpen,
        shareProfileTourOpen,
        handleCloseTour,
        editAboutMeDialogOpen,
        setEditAboutMeDialogOpen,
        editResumeDialogOpen,
        setEditResumeDialogOpen,
        editVideoOverviewDialogOpen,
        setEditVideoOverviewDialogOpen,
        editPortfolioDialogOpen,
        setEditPortfolioDialogOpen,
        confirmEditProfileDialogOpen,
        setConfirmEditProfileDialogOpen,
        handleConfirmEditProfile,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context }}>
            <AfitdPageHead title={PAGE_TITLE_JS_MY_PROFILE} />
            {/* <div {...defns.root}>
                <SplitPane split="horizontal" allowResize={false} defaultSize={128} pane2Style={{ overflow: 'hidden' }}>
                    <div {...defns.header}> */}
            <Grid {...defns.root}>
                <Grid {...defns.header}>
                    <div {...defns.headerLeft}>
                        <div {...defns.headerTitle}>
                            <div {...defns.title}>{DASHBOARD_MY_PROFILE}</div>
                            {lgMatches ? (
                                <ShareButtonMenu open={open} handleOpenMenu={handleOpenMenu} handleCloseMenu={handleCloseMenu} anchorEl={anchorEl} smMatches={smMatches} />
                            ) : (
                                <div {...defns.shareContainer} data-tour="js-share-button-container">
                                    <IconButton {...defns.shareIcon}>
                                        <ShareIcon />
                                    </IconButton>
                                    <AfitdTypography {...defns.shareText} />
                                    <div {...defns.slideContainer}>
                                        <Slide direction="right" in={shareButtonsShowing} timeout={500} mountOnEnter unmountOnExit>
                                            <ShareButtonBar {...defns.shareButtonBar} />
                                        </Slide>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div {...defns.headerToday}>
                            <CalendarTodayIcon {...defns.todayIcon} />
                            <div {...defns.todayContainer}>
                                <AfitdTypography {...defns.todayText} />
                            </div>
                        </div>
                    </div>
                    <div {...defns.headerRight}>
                        <div {...defns.headerButtons}>
                            {/* <div>
                                <HelpOutlineIcon {...defns.helpOutlineIcon} />
                            </div> */}
                            <div>
                                <AfitdButtonGroup variant="contained" aria-label="contained button group" {...defns.publicDraftButtonGroup}>
                                    <AfitdButton isGroup={true} {...defns.draftButton}>
                                        Draft
                                    </AfitdButton>
                                    <div data-tour="js-my-profile-public-button-container">
                                        <AfitdButton isGroup={true} {...defns.publicButton}>
                                            Public
                                        </AfitdButton>
                                    </div>
                                </AfitdButtonGroup>
                            </div>
                            <div {...defns.viewProfileButtonContainer} data-tour="js-view-profile-button-container">
                                <AfitdButtonGroup variant="contained" aria-label="contained button group" {...defns.viewProfileButtonGroup}>
                                    <AfitdButton isGroup={true} {...defns.viewProfileButton}>
                                        View Profile
                                    </AfitdButton>
                                </AfitdButtonGroup>
                                {/* <AfitdButton color="primary" label="View Profile" size="small" /> */}
                            </div>
                        </div>
                    </div>
                    {/* </div>
                    <div {...defns.body}> */}
                </Grid>
                <Grid {...defns.content}>
                    <Grid {...defns.profileLeft}>
                        <div {...defns.aboutMeLinkContainer}>
                            {smMatches ? <Link {...defns.aboutMeLink}>About...</Link> : <Link {...defns.aboutMeLink}>{PROFILE_ABOUT_ME}</Link>}
                        </div>
                        <div {...defns.resumeLinkContainer}>{smMatches ? <Link {...defns.resumeLink}>Resume...</Link> : <Link {...defns.resumeLink}>{PROFILE_RESUME}</Link>}</div>
                        <div {...defns.videoLinkContainer}>
                            {smMatches ? <Link {...defns.videoLink}>Video...</Link> : <Link {...defns.videoLink}>{PROFILE_VIDEO_OVERVIEW}</Link>}
                        </div>
                        <div {...defns.socialLinkContainer}>
                            {smMatches ? <Link {...defns.socialLink}>Social...</Link> : <Link {...defns.socialLink}>{PROFILE_SOCIAL_MEDIA}</Link>}
                        </div>
                    </Grid>
                    {lgMatches ? (
                        <Grid {...defns.profileLeftDivider}>
                            <Divider />
                        </Grid>
                    ) : null}
                    <Grid {...defns.profileRight}>
                        <SimpleBar style={{ height: '100%' }} scrollableNodeProps={{ ref: scrollableNodeRef }}>
                            <div {...defns.detailContainer} id="testContainer">
                                <AfitdScrollToElement name={SCROLL_TO_ELEMENT_ABOUT_ME} setRef={setAboutMeScrollToElementRef}>
                                    <AboutMe {...defns.aboutMe} />
                                </AfitdScrollToElement>
                                <AfitdScrollToElement name={SCROLL_TO_ELEMENT_RESUME} setRef={setResumeScrollToElementRef}>
                                    <Resume {...defns.resume} />
                                </AfitdScrollToElement>
                                <AfitdScrollToElement name={SCROLL_TO_ELEMENT_VIDEO_OVERVIEW} setRef={setVideoOverviewScrollToElementRef}>
                                    <VideoOverview {...defns.videoOverview} />
                                </AfitdScrollToElement>
                                <AfitdScrollToElement name={SCROLL_TO_ELEMENT_SOCIAL_MEDIA_PORTFOLIO} setRef={setSocialMediaScrollToElementRef}>
                                    <SocialMediaPortfolio {...defns.socialMediaPortfolio} />
                                </AfitdScrollToElement>
                            </div>
                        </SimpleBar>
                    </Grid>
                </Grid>
                <Grid {...defns.bottomFooter}>{mdMatches ? <Footer /> : null}</Grid>
            </Grid>
            <Dialog aria-labelledby="confirm-public-dialog" aria-describedby="confirm-public-dialog" {...defns.confirmDialog}>
                <DialogTitle id="alert-dialog-title">{defns.confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <AfitdTypography variant="body1">{defns.confirmDialog.message}</AfitdTypography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <AfitdButton {...defns.confirmDialogCancelButton}>No</AfitdButton>
                    <AfitdButton {...defns.confirmDialogOkButton}>Yes</AfitdButton>
                </DialogActions>
            </Dialog>
            {profileDialogOpen && <ProfileDialog {...defns.profileDialog} />}
            {/* <AfitdConfirmDialog {...defns.minOneResumeRequiredDialog} /> */}
            <AfitdMessageDialog {...defns.minOneResumeRequiredDialog} />
            <AfitdConfirmDialog {...defns.confirmEditProfileDialog} />
            <AfitdMessageDialog {...defns.badContentDialog} />
            <AfitdTour {...defns.walkThroughTour} />

            <SpeedDial
                ariaLabel="Edit Profile Speed Dial"
                // className={classes.editSpeedDial}
                hidden={false}
                icon={<SpeedDialIcon icon={<EditIcon />} openIcon={<CloseIcon />} />}
                onClose={handleCloseEditSpeedDial}
                onOpen={handleOpenEditSpeedDial}
                open={editSpeedDialOpen}
                {...defns.editSpeedDial}
                // className={classes.editSpeedDial}
            >
                {editSpeedDialActions.map(action => (
                    <SpeedDialAction
                        key={action.name}
                        icon={action.icon}
                        tooltipTitle={action.name}
                        tooltipOpen
                        classes={{ fab: classes.actionFab, staticTooltipLabel: classes.editSpeedDialTooltipLabel }}
                        onClick={action.onClick}
                        {...defns.editSpeedDialAction}
                    />
                ))}
            </SpeedDial>
        </LayoutContext.Provider>
    );
};

export default MyProfile;
